// The page's AI beside the open page, on a wide screen (docs/prime-panel.md).
// The thread is the page's own (PageThread: the same one the phone's Agent tab
// shows), so every turn reads the page and edits it with page_write cards; the
// canvas shows what is drawn on it; the board is for sketching. Below
// PANEL_QUERY it is not drawn at all: the page's details column or the phone's
// Agent tab carries the same thread.
import { PrimeMark } from '@/components/PrimeMark'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { usePageThread } from '@/db/hooks'
import type { PageResource } from '@/kinds/page/apply'
import type { EditorApi } from '@/kinds/page/editor'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { canvasesOf, PANEL_QUERY, type PanelTab } from '@/lib/panel'
import { usePanel } from '@/state/panel'
import { useAccess } from '@/state/access'
import { QUICK_CHAT } from '@/lib/lead'
import { Button, cx } from './ui'
import { CanvasView } from './CanvasView'
import { Whiteboard } from './Whiteboard'
import { DockAside, DockHandle } from './Dock'
import { PageThread } from './PageThread'
import { useAssistantEnabled } from './Suggestions'

// one name for the page's thread wherever it shows (QUICK_CHAT); the whiteboard is a sketch,
// not the retired overview Board
const TAB_LABEL: Record<PanelTab, string> = { thread: QUICK_CHAT, canvas: 'Canvas', board: 'Sketch' }

export const PrimePanel = ({
  page,
  editorApi,
  focusSignal = 0,
}: {
  page: PageResource
  editorApi: EditorApi | null
  focusSignal?: number
}) => {
  const wide = useMediaQuery(PANEL_QUERY)
  const enabled = useAssistantEnabled()
  // a workspace member has her own Prime in her own app
  const member = useAccess((s) => s.member)
  const open = usePanel((s) => s.open)
  const toggle = usePanel((s) => s.toggle)
  const usable = wide && !!enabled && !member

  // Mod+/ shows and hides it, even from inside the editor
  useEffect(() => {
    if (!usable) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key === '/') {
        e.preventDefault()
        usePanel.getState().toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [usable])

  if (!usable) return null
  if (!open)
    return (
      <DockHandle
        onClick={toggle}
        label="Show this page’s AI"
        title="Show the page’s AI (Ctrl or ⌘ + /)"
        testId="prime-panel-handle"
      >
        <PrimeMark size={14} aria-hidden />
      </DockHandle>
    )
  return <Docked page={page} editorApi={editorApi} focusSignal={focusSignal} />
}

const Docked = ({
  page,
  editorApi,
  focusSignal,
}: {
  page: PageResource
  editorApi: EditorApi | null
  focusSignal: number
}) => {
  const width = usePanel((s) => s.width)
  const tab = usePanel((s) => s.tab)
  const setTab = usePanel((s) => s.setTab)
  const setWidth = usePanel((s) => s.setWidth)
  const toggle = usePanel((s) => s.toggle)
  const session = usePageThread(page.id)
  const canvases = useMemo(() => canvasesOf(session?.spec.messages ?? []), [session])

  // something new on the canvas is the point of the turn: go and look
  const seen = useRef<number | null>(null)
  const [pick, setPick] = useState<number | null>(null)
  useEffect(() => {
    if (seen.current !== null && canvases.length > seen.current) {
      setTab('canvas')
      setPick(null)
    }
    seen.current = canvases.length
  }, [canvases.length, setTab])
  const shown = canvases[pick ?? canvases.length - 1]

  return (
    <DockAside width={width} onWidth={setWidth} label="This page’s AI" testId="prime-panel">
      <header className="flex items-center gap-1 border-b border-line px-3 py-2">
        <PrimeMark size={14} className="text-accent shrink-0" aria-hidden />
        <div className="flex flex-1 gap-1" role="tablist">
          {(Object.keys(TAB_LABEL) as PanelTab[]).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={cx(
                'rounded-full px-3 min-h-8 text-sm',
                tab === t ? 'bg-accent-soft text-fg' : 'text-muted hover:text-fg',
              )}
              data-testid={`prime-panel-tab-${t}`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={toggle}
          className="rounded-full p-2 text-muted hover:text-fg"
          aria-label="Move the page’s AI out of the way"
          title="Hide (Ctrl or ⌘ + /)"
          data-testid="prime-panel-hide"
        >
          <ChevronRight size={16} aria-hidden />
        </button>
      </header>

      {tab === 'thread' && (
        <PageThread
          page={page}
          editorApi={editorApi}
          panel
          focusSignal={focusSignal}
          onSent={() => setTab('thread')}
        />
      )}
      {tab !== 'thread' && (
        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
          {tab === 'canvas' &&
            (shown ? (
              <div className="flex h-full flex-col gap-2" data-testid="prime-panel-canvas">
                <div className="flex items-center justify-between gap-2">
                  <span className="label text-muted truncate">{shown.title || 'Canvas'}</span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    {canvases.length > 1 && (
                      <span className="flex items-center gap-1 text-xs text-faint">
                        <Button
                          variant="chip"
                          disabled={(pick ?? canvases.length - 1) === 0}
                          onClick={() => setPick((pick ?? canvases.length - 1) - 1)}
                          aria-label="Earlier canvas"
                        >
                          ‹
                        </Button>
                        <Button
                          variant="chip"
                          disabled={pick === null || pick >= canvases.length - 1}
                          onClick={() => setPick(pick! + 1 >= canvases.length - 1 ? null : pick! + 1)}
                          aria-label="Later canvas"
                        >
                          ›
                        </Button>
                      </span>
                    )}
                    {shown.kind === 'mermaid' && editorApi && (
                      <Button
                        variant="chip"
                        onClick={() =>
                          void editorApi.insertMarkdown(
                            '```mermaid\n' + shown.content + '\n```',
                            'after-cursor',
                          )
                        }
                        data-testid="canvas-insert"
                      >
                        Put in page
                      </Button>
                    )}
                  </span>
                </div>
                <div className="min-h-0 flex-1">
                  <CanvasView canvas={shown} />
                </div>
              </div>
            ) : (
              <p className="text-xs text-faint" data-testid="prime-panel-canvas-empty">
                Nothing here yet. Ask for a diagram, a sketch of a layout or a mock-up, and it shows here.
              </p>
            ))}
          {tab === 'board' && <Whiteboard key={page.id} pageId={page.id} />}
        </div>
      )}
    </DockAside>
  )
}
